<template>
  <div class="usage">
    <div>
      <v-layout column align-center>
        <v-container fluid>
          <v-row justify="center">
            <v-form ref="form" lazy-validation>
              <div
                class="d-flex py-5 align-center justify-center"
                v-if="!this.order_usage"
              >
                <date-picker
                  @change="
                    usage_range_start = $event;
                    refreshUsage();
                  "
                  :initialValue="start_date"
                  label="Period Start"
                  textColor="primary"
                  dateColor="primary"
                  dateBtnColor="primary"
                  :rules="startDateRules"
                  ref="startDatePicker"
                  class="pr-5"
                ></date-picker>

                <date-picker
                  @change="
                    usage_range_end = $event;
                    refreshUsage();
                  "
                  :initialValue="end_date"
                  label="Period End"
                  textColor="primary"
                  dateColor="primary"
                  dateBtnColor="primary"
                  :rules="endDateRules"
                  ref="endDatePicker"
                  class="pl-5"
                ></date-picker>

                <v-spacer class="pr-15"></v-spacer>

                <account-switch
                  v-show="support_accounts.length > 1"
                  :accounts="support_accounts"
                  @switch_account="switch_account($event.account_id)"
                ></account-switch>

                <user-switch
                  v-show="users.length > 1"
                  :users="users"
                  @switch_user="switch_user($event.user_id)"
                ></user-switch>
              </div>
            </v-form>
          </v-row>
          <v-row>
            <v-col>
              <!-- cost -->
              <v-card max-width="1000" elevation="4" class="mx-auto px-5 pb-10">
                <v-card-title>Cost</v-card-title>

                <v-data-table
                  v-if="this.usage_date"
                  :headers="cost_headers"
                  :items="grouped_cost"
                  item-key="category_name"
                  :loading="loading"
                  hide-default-footer
                  dense
                  class="elevation-1"
                >
                  <template v-slot:no-data>
                    <span v-if="!usage_range_start || !usage_range_start"
                      >Please select a date range</span
                    >
                    <span v-else>No data</span>
                  </template>
                  <template v-slot:[`item.amount`]="{ item }">
                    {{ Number(item.amount).toLocaleString() }}
                  </template>
                </v-data-table>

                <v-data-table
                  v-if="!this.usage_date"
                  :headers="order_cost_headers"
                  :items="order_grouped_cost"
                  item-key="category_name"
                  :loading="loading"
                  hide-default-footer
                  dense
                  class="elevation-1"
                >
                  <template v-slot:no-data>
                    <span v-if="!usage_range_start || !usage_range_start"
                      >Please select a date range</span
                    >
                    <span v-else>No data</span>
                  </template>

                  <template v-slot:[`item.amount`]="{ item }">
                    {{ Number(item.amount).toLocaleString() }}
                  </template>
                </v-data-table>

                <bar-chart
                  v-if="
                    order_grouped_cost.length > 0 &&
                    !order_usage &&
                    !check_non_zero_cost &&
                    barDataCollection &&
                    barDataCollection.datasets &&
                    barDataCollection.datasets.length > 0
                  "
                  :chartData="barDataCollection"
                />
              </v-card>

              <v-spacer class="pt-5"></v-spacer>

              <!-- area -->
              <v-card max-width="1000" elevation="4" class="mx-auto px-5 pb-10">
                <v-card-title>Area</v-card-title>

                <v-data-table
                  :headers="area_headers"
                  :items="grouped_area"
                  item-key="category_name"
                  :loading="loading"
                  hide-default-footer
                  dense
                  class="elevation-1"
                >
                  <template v-slot:no-data>
                    <span v-if="!usage_range_start || !usage_range_start"
                      >Please select a date range</span
                    >
                    <span v-else>No data</span>
                  </template>
                  <template v-slot:[`item.area`]="{ item }">
                    {{ Number(item.area).toLocaleString() }}
                  </template>
                </v-data-table>
                <doughnut-chart
                  v-if="
                    grouped_area.length > 0 &&
                    !order_usage &&
                    !check_non_zero_area &&
                    doughnutDataCollection &&
                    doughnutDataCollection.datasets &&
                    doughnutDataCollection.datasets.length > 0
                  "
                  :chartData="doughnutDataCollection"
                />
              </v-card>

              <!-- tasking -->
              <v-card
                max-width="1000"
                elevation="4"
                class="mx-auto mt-8 px-5 pb-10"
              >
                <v-card-title>Tasking</v-card-title>

                <v-data-table
                  :headers="tasking_headers"
                  :items="grouped_tasking"
                  item-key="category_name"
                  :loading="loading"
                  hide-default-footer
                  dense
                  class="elevation-1"
                >
                  <template v-slot:no-data>
                    <span v-if="!usage_range_start || !usage_range_start"
                      >Please select a date range</span
                    >
                    <span v-else>No data</span>
                  </template>
                  <template v-slot:[`item.area`]="{ item }">
                    {{ Number(item.area).toLocaleString() }}
                  </template>
                </v-data-table>
              </v-card>

              <v-card
                v-if="users_usage"
                max-width="1000"
                elevation="4"
                class="mx-auto mt-8 px-5 pb-10"
              >
                <v-card-title>User Breakdown</v-card-title>
                <v-data-table
                  :headers="users_headers"
                  :items="users_usage"
                  item-key="user_id"
                  class="elevation-1"
                  :loading="loading"
                  :page="current_page"
                  :items-per-page="5"
                  hide-default-footer
                >
                  <template v-slot:[`item.actions`]="{ item }">
                    <user-actions
                      :user="item"
                      show_view
                      show_account
                      show_usage
                      show_orders
                    ></user-actions>
                  </template>
                  <template v-slot:[`item.fresh_cost`]="{ item }">
                    {{ Number(item.fresh_cost).toLocaleString() }}
                  </template>
                  <template v-slot:[`item.standard_cost`]="{ item }">
                    {{ Number(item.standard_cost).toLocaleString() }}
                  </template>
                  <template v-slot:[`item.training_cost`]="{ item }">
                    {{ Number(item.training_cost).toLocaleString() }}
                  </template>
                  <template v-slot:[`item.total_cost`]="{ item }">
                    {{ Number(item.total_cost).toLocaleString() }}
                  </template>
                  <template v-slot:footer>
                    <div><v-divider></v-divider></div>
                    <v-layout align-center justify-end>
                      <v-btn
                        class="ma-4 button_text--text"
                        small
                        elevation="4"
                        :disabled="current_page === 1"
                        @click="current_page -= 1"
                        color="primary"
                      >
                        <v-icon> mdi-chevron-left </v-icon>
                      </v-btn>

                      <span class="mx-2">{{ current_page }}</span>

                      <v-btn
                        class="ma-4 button_text--text"
                        small
                        elevation="4"
                        :disabled="
                          current_page === users_usage_last_page ||
                          users_usage.length === 0
                        "
                        @click="current_page += 1"
                        color="primary"
                      >
                        <v-icon> mdi-chevron-right </v-icon>
                      </v-btn>
                    </v-layout>
                  </template>

                  <template v-slot:no-data>
                    <span v-if="!usage_range_start || !usage_range_start"
                      >Please select a date range</span
                    >
                    <span v-else>No data</span>
                  </template>
                </v-data-table>
              </v-card>
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <div class="float-right caption" v-if="this.usage_date">
                As of {{ info_date }}
              </div>
            </v-col>
          </v-row>
        </v-container>
      </v-layout>
    </div>
  </div>
</template>

<script>
import DoughnutChart from "@/components/usage/DoughnutChart.vue";
import BarChart from "@/components/usage/StackedBarChart.vue";
import DatePicker from "@/components/shared/DatePicker.vue";
import UserActions from "@/components/user/UserActions.vue";
import AccountSwitch from "@/components/account/AccountSwitch.vue";
import UserSwitch from "@/components/user/UserSwitch.vue";
import adminApi from "@/api/admin";
import utils from "@/utils/userType";
import _ from "lodash";
import { mapGetters } from "vuex";

export default {
  props: {
    usage: Object,
    tasking_usage: Object,
    users_usage: Array,
    usage_date: String,
    start_date: String,
    end_date: String,
    loading: Boolean,
    order_usage: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    DatePicker,
    UserActions,
    AccountSwitch,
    UserSwitch,
    DoughnutChart,
    BarChart,
  },
  data() {
    return {
      usage_range_start: this.start_date,
      usage_range_end: this.end_date,
      current_page: 1,
      support_accounts: [],
      users: [],
    };
  },
  mounted() {
    if (this.is_admin_or_support && this.$route.params.user_id) {
      this.getUsers();
    }
    if (this.$route.params.account_id && !this.$route.params.user_id) {
      this.waitThenGetAccounts();
    }
    this.validateDates();
  },
  computed: {
    info_date() {
      if (this.usage_date) {
        return new Date(this.usage_date).toLocaleString();
      } else {
        return new Date().toLocaleString();
      }
    },
    area_headers() {
      return [
        { text: "Category", value: "category_name", filterable: false },
        { text: "Sq.Km", value: "area", filterable: false },
      ];
    },
    tasking_headers() {
      return [
        { text: "Amount ($)", value: "amount", filterable: false },
        { text: "Limit ($)", value: "limit", filterable: false },
        { text: "Available ($)", value: "available", filterable: false },
        { text: "Sq.Km", value: "sq_km", filterable: false },
      ];
    },
    grouped_area() {
      if (!this.usage || !this.usage.area) {
        return [];
      }
      return [
        {
          category_name: "Total",
          area: _.get(this.usage, "area.total_imagery_sqkm", null),
        },
        {
          category_name: "Fresh",
          area: _.get(this.usage, "area.fresh_imagery_sqkm", null),
        },
        {
          category_name: "Standard",
          area: _.get(this.usage, "area.standard_imagery_sqkm", null),
        },
        {
          category_name: "Training",
          area: _.get(this.usage, "area.training_imagery_sqkm", null),
        },
      ];
    },
    grouped_tasking() {
      if (!this.tasking_usage || !this.tasking_usage.area) {
        return [];
      }

      return [
        {
          category_name: "Tasking",
          amount: _.get(this.tasking_usage, "cost.tasking_imagery_cost", null),
          limit: this.formatUsageValue(
            _.get(this.tasking_usage, "limits.tasking_imagery_fee_limit", null)
          ),
          available: this.formatUsageValue(
            _.get(this.tasking_usage, "available.tasking_imagery_balance", null)
          ),
          sq_km: _.get(this.tasking_usage, "area.tasking_imagery_sqkm", null),
        },
      ];
    },
    cost_headers() {
      return [
        { text: "Category", value: "category_name", filterable: false },
        { text: "Amount ($)", value: "amount", filterable: false },
        { text: "Limit ($)", value: "limit", filterable: false },
        { text: "Available ($)", value: "available", filterable: false },
      ];
    },
    grouped_cost() {
      if (!this.usage) {
        return [];
      }
      return [
        {
          category_name: "Total",
          amount: _.get(this.usage, "cost.total_imagery_cost", null),
          limit: this.formatUsageValue(
            _.get(this.usage, "limits.annual_subscription_fee_limit", null)
          ),
          available: this.formatUsageValue(
            _.get(this.usage, "available.total_imagery_balance", null)
          ),
        },
        {
          category_name: "Fresh",
          amount: _.get(this.usage, "cost.fresh_imagery_cost", null),
          limit: this.formatUsageValue(
            _.get(this.usage, "limits.fresh_imagery_fee_limit", null)
          ),
          available: this.formatUsageValue(
            _.get(this.usage, "available.fresh_imagery_balance", null)
          ),
        },
        {
          category_name: "Standard",
          amount: _.get(this.usage, "cost.standard_imagery_cost", null),
          limit: this.formatUsageValue(
            _.get(this.usage, "limits.standard_imagery_fee_limit", null)
          ),
          available: this.formatUsageValue(
            _.get(this.usage, "available.standard_imagery_balance", null)
          ),
        },
        {
          category_name: "Training",
          amount: _.get(this.usage, "cost.training_imagery_cost", null),
          limit: this.formatUsageValue(
            _.get(this.usage, "limits.training_imagery_fee_limit", null)
          ),
          available: this.formatUsageValue(
            _.get(this.usage, "available.training_imagery_balance", null)
          ),
        },
      ];
    },
    order_cost_headers() {
      return [
        { text: "Category", value: "category_name", filterable: false },
        { text: "Amount ($)", value: "amount", filterable: false },
      ];
    },
    order_grouped_cost() {
      if (!this.usage || !this.usage.cost) {
        return [];
      }
      return [
        {
          category_name: "Total",
          amount: _.get(this.usage, "cost.total_imagery_cost", null),
        },
        {
          category_name: "Fresh",
          amount: _.get(this.usage, "cost.fresh_imagery_cost", null),
        },
        {
          category_name: "Standard",
          amount: _.get(this.usage, "cost.standard_imagery_cost", null),
        },
        {
          category_name: "Training",
          amount: _.get(this.usage, "cost.training_imagery_cost", null),
        },
      ];
    },
    check_non_zero_cost() {
      if (_.get(this.usage, "cost.training_imagery_cost", null) === 0) {
        return true;
      } else {
        return false;
      }
    },
    check_non_zero_area() {
      if (_.get(this.usage, "area.total_imagery_sqkm", null) === 0) {
        return true;
      } else {
        return false;
      }
    },
    users_headers() {
      return [
        { text: "Name", value: "name" },
        { text: "Fresh Cost ($)", value: "fresh_cost" },
        { text: "Standard Cost ($)", value: "standard_cost" },
        { text: "Training Cost ($)", value: "training_cost" },
        { text: "Total Cost ($)", value: "total_cost" },
        { text: "Actions", value: "actions", sortable: false },
      ];
    },

    users_usage_last_page() {
      return Math.ceil(this.users_usage.length / 5);
    },

    is_admin_or_support() {
      return utils.is_admin_or_support(this.user_details);
    },

    ...mapGetters(["user_details", "account_details"]),

    startDateRules() {
      return [
        (v) =>
          v &&
          this.usage_range_end &&
          new Date(v).getTime() > new Date(this.usage_range_end).getTime()
            ? "Period Start must be before Period End"
            : true,
      ];
    },

    endDateRules() {
      return [
        (v) =>
          v &&
          this.usage_range_start &&
          new Date(v).getTime() < new Date(this.usage_range_start).getTime()
            ? "Period End must be after Period Start"
            : true,
      ];
    },
    barDataCollection() {
      if (
        _.get(this.usage, "available.total_imagery_balance", null) ||
        _.get(this.usage, "available.fresh_imagery_balance", null) ||
        _.get(this.usage, "available.standard_imagery_balance", null) ||
        _.get(this.usage, "available.training_imagery_balance", null)
      ) {
        return {
          labels: ["Total", "Fresh", "Standard", "Training"],
          datasets: [
            {
              backgroundColor: ["#98DDCA", "#B4FF9F", "#FFB476", "#FFAAA7"],
              data: [
                _.get(this.usage, "cost.total_imagery_cost", null),
                _.get(this.usage, "cost.fresh_imagery_cost", null),
                _.get(this.usage, "cost.standard_imagery_cost", null),
                _.get(this.usage, "cost.training_imagery_cost", null),
              ],
            },
          ],
        };
      }
      return {
        labels: ["Total", "Fresh", "Standard", "Training"],
        datasets: [
          {
            backgroundColor: ["#98DDCA", "#B4FF9F", "#FFB476", "#FFAAA7"],
            data: [
              _.get(this.usage, "cost.total_imagery_cost", null),
              _.get(this.usage, "cost.fresh_imagery_cost", null),
              _.get(this.usage, "cost.standard_imagery_cost", null),
              _.get(this.usage, "cost.training_imagery_cost", null),
            ],
          },
          {
            backgroundColor: ["#EFEFEF"],
            data: [
              _.get(this.usage, "available.total_imagery_balance", null),
              _.get(this.usage, "available.fresh_imagery_balance", null),
              _.get(this.usage, "available.standard_imagery_balance", null),
              _.get(this.usage, "available.training_imagery_balance", null),
            ],
          },
        ],
      };
    },
    doughnutDataCollection() {
      return {
        labels: ["Fresh (Sq.Km)", "Standard (Sq.Km)", "Training (Sq.Km)"],
        datasets: [
          {
            backgroundColor: ["#B4FF9F", "#FFB476", "#FFAAA7"],
            borderWidth: 2,
            borderColor: "#1D1C1D",
            data: [
              _.get(this.usage, "area.fresh_imagery_sqkm", null),
              _.get(this.usage, "area.standard_imagery_sqkm", null),
              _.get(this.usage, "area.training_imagery_sqkm", null),
            ],
          },
        ],
      };
    },
  },
  watch: {
    $route() {
      this.$refs.startDatePicker.setDateValue(this.$route.query.start_date);
      this.$refs.endDatePicker.setDateValue(this.$route.query.end_date);
    },
  },
  methods: {
    formatUsageValue(val) {
      return Number(val) === -1 ? "Unlimited" : Number(val).toLocaleString();
    },
    validateDates() {
      if (this.usage_range_start || this.usage_range_end) {
        this.$refs.form.validate();
      }
    },
    refreshUsage() {
      this.validateDates();

      if (
        this.usage_range_start === this.$route.query.start_date &&
        this.usage_range_end === this.$route.query.end_date
      ) {
        // same route so emit changed event to refresh the data
        this.$emit("usage_range_changed", {
          start: this.usage_range_start,
          end: this.usage_range_end,
        });
      } else {
        // new dates so update the route which in turn refreshes the data
        this.$router
          .push({
            query: {
              start_date: this.usage_range_start,
              end_date: this.usage_range_end,
            },
          })
          .catch(() => {
            // catch redundant path error
          });
      }
    },
    switch_account(account_id) {
      // hard copy router query params
      let query_params = JSON.parse(JSON.stringify(this.$route.query));
      // add selected account to query params
      this.$router.push({
        params: { account_id: account_id },
        query: query_params,
      });
    },
    switch_user(user_id) {
      // hard copy router query params
      let query_params = JSON.parse(JSON.stringify(this.$route.query));
      // add selected account to query params
      this.$router.push({
        params: { user_id: user_id },
        query: query_params,
      });
    },
    waitThenGetAccounts() {
      if (this.user_details.account_id) {
        if (
          ("support_accounts" in this.user_details &&
            this.user_details.support_accounts.length > 0) ||
          this.user_details.super_admin
        ) {
          adminApi
            .fetch_all_accounts()
            .then((response) => {
              this.support_accounts = response;
            })
            .catch((error) => {
              return error;
            });
        }
      } else {
        setTimeout(this.waitThenGetAccounts, 200);
      }
    },
    getUsers() {
      const account_id = this.$route.params.account_id;
      adminApi
        .fetch_all_users(account_id)
        .then((response) => {
          this.users = response;
        })
        .catch(() => {
          // let interceptor raise error
        });
    },
  },
};
</script>
